import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import { marketsApi } from '../api/markets-api';
import { marketKeys } from '../hooks/market-keys';

/**
 * The platform's exchange rates, read-only: a market in a currency missing here cannot be
 * created, and only the platform team can add one.
 */
export function ExchangeRatesCard() {
  const { t } = useTranslation('markets');
  const format = useFormat();
  const query = useQuery({
    queryKey: marketKeys.exchangeRates(),
    queryFn: marketsApi.exchangeRates,
  });

  return (
    <FormSection
      title={t('rates.title')}
      description={t('rates.description')}
      className="mt-6"
      id={SECTION_ANCHORS.exchangeRates}
      tour="settings.markets.exchange-rates"
    >
      {query.isPending ? (
        <Skeleton className="h-24" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : query.data.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('rates.empty')}</p>
      ) : (
        <div className="-mx-6 border-t">
          <Table aria-label={t('rates.title')}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">{t('rates.columns.pair')}</TableHead>
                <TableHead className="text-right">{t('rates.columns.rate')}</TableHead>
                <TableHead>{t('rates.columns.source')}</TableHead>
                <TableHead className="pr-6">{t('rates.columns.updated')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.map((rate) => (
                <TableRow key={`${rate.baseCurrency}-${rate.quoteCurrency}`}>
                  <TableCell className="pl-6 font-mono text-[13px]">
                    {rate.baseCurrency} → {rate.quoteCurrency}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {format.number(rate.rate, { maximumFractionDigits: 6 })}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{rate.source}</TableCell>
                  <TableCell
                    className="text-muted-foreground pr-6"
                    title={format.utc(rate.fetchedAt)}
                  >
                    {format.relative(rate.fetchedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </FormSection>
  );
}
